/**
 * Owns the canvas, the WebGPU canvas context, and the resize lifecycle.
 *
 * Follows MDN's recommendation: canvas format is always
 * navigator.gpu.getPreferredCanvasFormat(). Anything else can incur extra
 * texture copies in the compositor.
 */

export type Surface = {
  canvas: HTMLCanvasElement;
  context: GPUCanvasContext;
  format: GPUTextureFormat;
  /** CSS-pixel size of the canvas (width x height). */
  cssWidth: number;
  cssHeight: number;
  /** Backing store size in device pixels (canvas.width x canvas.height). */
  width: number;
  height: number;
  /** internalRenderScale, applied to the offscreen feedback chain only. */
  internalRenderScale: number;
  /** Width/height of the internal render targets. */
  renderWidth: number;
  renderHeight: number;
};

export type SurfaceOptions = {
  internalRenderScale?: number;
  maxDevicePixelRatio?: number;
};

const DEFAULT_INTERNAL_SCALE = 0.75;
const DEFAULT_MAX_DPR = 1.5;

export function attachSurface(
  device: GPUDevice,
  canvas: HTMLCanvasElement,
  options: SurfaceOptions = {},
): Surface {
  const format = navigator.gpu.getPreferredCanvasFormat();
  const context = canvas.getContext('webgpu');
  if (!context) {
    throw new Error('canvas.getContext("webgpu") returned null');
  }

  context.configure({
    device,
    format,
    alphaMode: 'opaque',
    usage: GPUTextureUsage.RENDER_ATTACHMENT,
  });

  const internalRenderScale = options.internalRenderScale ?? DEFAULT_INTERNAL_SCALE;
  const maxDpr = options.maxDevicePixelRatio ?? DEFAULT_MAX_DPR;

  const surface: Surface = {
    canvas,
    context,
    format,
    cssWidth: 0,
    cssHeight: 0,
    width: 0,
    height: 0,
    internalRenderScale,
    renderWidth: 0,
    renderHeight: 0,
  };

  applyResize(surface, maxDpr);
  return surface;
}

/** Recomputes pixel sizes from the current CSS size. Returns true if changed. */
export function applyResize(surface: Surface, maxDpr = DEFAULT_MAX_DPR): boolean {
  const dpr = Math.min(window.devicePixelRatio || 1, maxDpr);
  const rect = surface.canvas.getBoundingClientRect();
  const cssW = Math.max(1, Math.floor(rect.width));
  const cssH = Math.max(1, Math.floor(rect.height));
  const w = Math.max(1, Math.floor(cssW * dpr));
  const h = Math.max(1, Math.floor(cssH * dpr));

  if (surface.width === w && surface.height === h) return false;

  surface.cssWidth = cssW;
  surface.cssHeight = cssH;
  surface.width = w;
  surface.height = h;
  surface.canvas.width = w;
  surface.canvas.height = h;

  surface.renderWidth = Math.max(2, Math.floor(w * surface.internalRenderScale));
  surface.renderHeight = Math.max(2, Math.floor(h * surface.internalRenderScale));
  return true;
}

/**
 * Subscribes to window resize events; calls `onChange(surface)` whenever the
 * backing size actually changes. Returns an unsubscribe function.
 */
export function watchResize(
  surface: Surface,
  onChange: (surface: Surface) => void,
  maxDpr = DEFAULT_MAX_DPR,
): () => void {
  const handler = () => {
    if (applyResize(surface, maxDpr)) onChange(surface);
  };
  window.addEventListener('resize', handler, { passive: true });
  const ro = new ResizeObserver(handler);
  ro.observe(surface.canvas);
  return () => {
    window.removeEventListener('resize', handler);
    ro.disconnect();
  };
}

export function setInternalRenderScale(surface: Surface, scale: number): void {
  surface.internalRenderScale = Math.max(0.25, Math.min(2.0, scale));
  surface.renderWidth = Math.max(2, Math.floor(surface.width * surface.internalRenderScale));
  surface.renderHeight = Math.max(2, Math.floor(surface.height * surface.internalRenderScale));
}
